import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import { getLanguageString as _ } from "misc/lang";
import { apiGetHrAwardClaims } from "net/requests";
import { Box, CircularProgress, Alert, Typography } from "@mui/material";
import FormButton from "components/form/FormButton";
import css from "./awardsscreen.module.scss";
import { Paths } from "appConfig";

const AwardWinnersScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { admin, navigate } = props;
  const { id } = useParams<{ id: string }>();

  const [winners, setWinners] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const loadWinners = useCallback(() => {
    if (!admin || !id) return;

    setLoading(true);
    setError(undefined);

    apiGetHrAwardClaims(admin, id)
      .then((response) => {
        setWinners(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Error loading award winners:", error);
        setError(_("awards", "error_loading_award_winners"));
        setWinners([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [admin, id]);

  useEffect(() => {
    loadWinners();
  }, [loadWinners]);

  const getWinnerName = (winner: any) =>
    winner.name ||
    winner.full_name ||
    winner.first_name ||
    winner.email ||
    _("awards", "unknown_winner");

  const getWinnerEmail = (winner: any) =>
    winner.email || winner.user_email || winner.claimant_email || "";

  const getWinnerDate = (winner: any) =>
    winner.claimed_at ||
    winner.created_at ||
    winner.updated_at ||
    winner.date ||
    "";

  return (
    <div className={css.container}>
      <PageHeader
        title={_("awards", "winners_title")}
        subtitle={_("awards", "winners_subtitle")}
      />

      <Box className={css.actionButtons}>
        <FormButton
          label={_("general", "back")}
          onSubmit={() => navigate(Paths.AWARDS)}
          inline={true}
        />
      </Box>

      <div className={css.contentBox}>
        {error && <Alert severity="error">{error}</Alert>}

        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && !error && winners.length === 0 && (
          <div className={css.emptyState}>
            <Typography className={css.title}>
              {_("awards", "no_winners_title")}
            </Typography>
            <Typography className={css.subtitle}>
              {_("awards", "no_winners_subtitle")}
            </Typography>
          </div>
        )}

        {!loading && winners.length > 0 && (
          <div className={css.awardsList}>
            {winners.map((winner, index) => (
              <div key={index} className={css.awardItem}>
                <div className={css.awardTitle}>{getWinnerName(winner)}</div>
                {getWinnerEmail(winner) && (
                  <div className={css.awardField}>
                    <strong>{_("awards", "field_email")}:</strong>{" "}
                    {getWinnerEmail(winner)}
                  </div>
                )}
                {getWinnerDate(winner) && (
                  <div className={css.awardField}>
                    <strong>{_("awards", "field_claimed_at")}:</strong>{" "}
                    {new Date(getWinnerDate(winner)).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AwardWinnersScreen;
